import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import I18n from "i18n-js";
import { useFetchUserData } from "app/hook";
import { LocalStorageKey } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { Loading, ActionButton } from "common/elements";
import { IconArrowLeft, IconArrowRight } from "common/assets";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { actions } from "features/user/slice";
import { TitleSvg } from "features/home/assets";
import { CreateChildUser } from "features/auth/CreateChildUser";
import { AppUser, UserType, AccountType } from "features/user/types";
import { UserList } from "./UserList";

export const UserSelection = () => {
  const TITLE_WIDTH = 828;
  const TITLE_HEIGHT = 182;
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [scroll, setScroll] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastActiveUserUid = localStorage.getItem(
    LocalStorageKey.LAST_ACTIVE_USER_UID,
  );
  const { handleFetchUserData } = useFetchUserData();
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const [showUserListDialog, setShowUserListDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const children = users
    .filter(
      (user) =>
        user.account_type !== AccountType.PARENTS &&
        user.type !== UserType.FROZEN,
    )
    .sort((a, b) => {
      if (a.uid === lastActiveUserUid) {
        return -1;
      }
      if (b.uid === lastActiveUserUid) {
        return 1;
      }
      return dayjs(a.last_sign_in_time) > dayjs(b.last_sign_in_time) ? -1 : 1;
    });

  useEffect(() => {
    if (children.length > 0 && !selectedUser) {
      setSelectedUser(children[0]);
    }
  }, [children, selectedUser]);

  const handleUserSelect = (user: AppUser) => () => {
    btnDelay(() => {
      play();
      setShowUserListDialog(false);
      setLoading(true);
      handleFetchUserData({ parent_uid: user.parent_uid, uid: user.uid })
        .then(() => {
          dispatch(actions.setActiveUser(user));
          navigate("/top", { replace: true });
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    });
  };

  const handleAddUser = () => {
    play();
    setCreateUserOpen(true);
  };

  const handleCloseCreateUser = () => {
    setCreateUserOpen(false);
  };

  const handleScrollBtn = (direction: number) => () => {
    if (scroll) return;
    play();
    setScroll(true);

    if (direction > 0) {
      // 右へ
      if (ref.current) {
        ref.current.scrollLeft += 176;
        setScroll(false);
      }
    } else {
      // 左へ
      if (ref.current) {
        ref.current.scrollLeft -= 176;
        setScroll(false);
      }
    }
  };

  const handleAddUserSuccess = (user: AppUser) => {
    setLoading(true);
    setCreateUserOpen(false);
    handleFetchUserData({ parent_uid: parent.uid, uid: user.uid })
      .then(() => {
        dispatch(actions.setActiveUser(user));
        navigate("/top", { replace: true });
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  return (
    <>
      {loading && <Loading />}
      <CreateChildUser
        open={createUserOpen}
        close={handleCloseCreateUser}
        onSuccess={handleAddUserSuccess}
      />
      <div className="flex-col-el flex-center relative w-full h-full gap-4">
        <div
          className="flex-col-view self-center"
          style={{
            width: TITLE_WIDTH * scale,
            height: TITLE_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-view origin-top-left"
            style={{
              width: TITLE_WIDTH,
              height: TITLE_HEIGHT,
              transform: `scale(${scale})`,
            }}
          >
            <TitleSvg />
          </div>
        </div>

        <div className="flex-col-center w-full">
          {children.length === 0 ? (
            <div className="flex-row-el flex-center min-w-0">
              <div className="flex-col-el flex-center gap-4 mt-4">
                <div className="text-center">
                  <p className="text-body text-gray-600">
                    {I18n.t("MSG_USER_START_LEARNING_MESSAGE")}
                  </p>
                </div>
                <ActionButton
                  type="primary"
                  Icon={
                    <span className="material-symbols-outlined !text-[28px] text-white">
                      person_add
                    </span>
                  }
                  text={I18n.t("MSG_USER_ADD_CHILD_ACCOUNT")}
                  onClick={handleAddUser}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="flex-col-el flex-center w-full">
                <div className="flex-row-el flex-center w-full p-4">
                  <div className="flex-col-el flex-center min-w-0">
                    <div className="flex-row-center px-4 py-2 bg-orange-alt rounded-full z-10">
                      <span className="material-symbols-outlined !text-[24px] text-textcolor-black">
                        group
                      </span>
                      <p className="text-body text-textcolor-black text-2xl !px-[8px]">
                        {I18n.t("MSG_USER_SELECTION_PROMPT")}
                      </p>
                    </div>

                    <div
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        boxShadow: "0px 1px 3px rgba(63, 74, 97, 0.3)",
                        marginTop: -30 * scale,
                      }}
                      className="flex-row-el flex-center backdrop-blur-[4px] w-full p-5 border-solid border-beige-alt border-8 rounded-[12px] gap-2"
                    >
                      {children.length > 4 && (
                        <div className="flex-row-el flex-center shrink-0 upsilon z-50 cursor-pointer pointer-events-auto">
                          <button
                            onClick={handleScrollBtn(-1)}
                            className="btn btn-primary w-[40px] h-[72px]"
                          >
                            <div className="flex-col-center">
                              <IconArrowLeft />
                            </div>
                          </button>
                        </div>
                      )}

                      <div
                        ref={ref}
                        className="flex-row-el item-center w-full overflow-x-auto gap-4 p-4"
                      >
                        {children.map((user) => (
                          <div
                            key={user.uid}
                            onClick={handleUserSelect(user)}
                            style={{
                              width: 375 * scale,
                              height: 375 * scale,
                            }}
                            className="flex-row-el flex-center cursor-pointer"
                          >
                            <div
                              style={{ transform: `scale(${scale})` }}
                              className="flex-row-el flex-center w-[375px] h-[375px] cursor-pointer"
                            >
                              <div
                                className="flex-col-el flex-center relative w-[300px] h-[300px] bg-white rounded-full z-10 active:shadow-active-show active:scale-95 transition-all duration-300"
                                style={{
                                  filter:
                                    "drop-shadow(1px 1px 2px rgba(63, 74, 97, 0.4))",
                                }}
                              >
                                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                                  <img
                                    key={user.icon}
                                    alt="ユーザプロフィール画像"
                                    src={user.icon}
                                    className="w-full h-full object-contain"
                                  />
                                </div>

                                <div
                                  className="flex-col-el flex-center absolute -bottom-3 w-[300px] h-[60px] px-2 bg-beige-10 rounded-[32px] text-textcolor-black"
                                  style={{
                                    boxShadow:
                                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                                  }}
                                >
                                  <p className="text-body text-3xl text-center text-body-ellipsis w-full">
                                    {user.display_name}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {children.length > 4 && (
                        <div className="flex-row-el flex-center shrink-0 upsilon z-50 cursor-pointer pointer-events-auto">
                          <button
                            onClick={handleScrollBtn(1)}
                            className="btn btn-primary w-[40px] h-[72px]"
                          >
                            <div className="flex-col-center">
                              <IconArrowRight />
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {children.length > 4 && (
                  <div
                    style={{ width: 380 * scale, height: 120 * scale }}
                    className="flex-row-el flex-center upsilon"
                  >
                    <div
                      onClick={() => setShowUserListDialog(true)}
                      style={{ transform: `scale(${scale})` }}
                      className="btn btn-primary flex-row-center relative rounded-2xl cursor-pointer"
                    >
                      <div className="flex-row-el flex-center py-2 w-full h-full">
                        <span className="material-symbols-outlined !text-xl text-white">
                          expand_circle_down
                        </span>

                        <div className="flex-col-el px-1">
                          <p className="text-body text-center text-white text-base w-full text-body-ellipsis">
                            {I18n.t("MSG_USER_LIST")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <UserList
          visible={showUserListDialog}
          close={() => setShowUserListDialog(false)}
          children={children}
          lastActiveUserUid={lastActiveUserUid}
          onUserSelect={handleUserSelect}
        />
      </div>
    </>
  );
};
